import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Folder02Icon, FolderSyncIcon, Link03Icon, Unlink04Icon } from '@hugeicons/react'
import { Edit } from '@mui/icons-material'
import { Alert, Box, Button, Card, CardContent, CircularProgress, IconButton, Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import PropTypes from 'prop-types'

import {
    useDisconnectGoogleDriveMutation,
    useLazyConnectGoogleDriveQuery,
    useLazyGetGoogleDriveTokenQuery,
} from '@/app/services/integrations'
import { useUpdateOrganisationMutation } from '@/app/services/organisation'
import { selectOrganisation, selectOrganisationId } from '@/app/slices/organisationSlice'
import { QUOTE_STATUS_MAP } from '@/common/utils'
import QuoteStatusChip from '@/features/quotes/components/common/QuoteStatusChip'
import useGoogleApi from '@/hooks/useGoogleApi'

import EditGoogleDriveStatusesDialog from './EditGoogleDriveStatusesDialog'

// Constants for Google API
const API_KEY = import.meta.env.VITE_GOOGLE_DRIVE_API_KEY
const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID
const APP_ID = import.meta.env.VITE_GOOGLE_APP_ID
const SCOPES = 'https://www.googleapis.com/auth/drive.file'

const googleDriveStatusesOptions = Object.entries(QUOTE_STATUS_MAP)
    .filter(([key]) => !['NotCalculated', 'Calculated', 'Dispatched'].includes(key))
    .map(([key, { index, label }]) => ({
        index,
        label,
        value: key,
    }))
    .sort((a, b) => a.index - b.index)

const styles = {
    card: {
        width: '100%',
        maxWidth: 440,
        mb: 2,
    },
    content: {
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mb: 1,
    },
    icon: {
        color: '#4285F4',
    },
    actions: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 2,
        mt: 2,
    },
    folderDisplay: {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        border: '1px solid',
        borderColor: 'action.active',
        borderRadius: 1,
        backgroundColor: 'action.hover',
    },
}

// Main component
const GoogleDriveIntegration = ({ disabled = false }) => {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const dispatch = useDispatch()

    const organisation = useSelector(selectOrganisation)
    const organisationId = useSelector(selectOrganisationId)

    // Get Google Drive settings from organisation data
    const googleDriveEnabled = useMemo(() => organisation?.googleDriveEnabled || false, [organisation])
    const googleDriveSubFolder = useMemo(() => organisation?.googleDriveSubFolder || null, [organisation])
    const enabledStatuses = useMemo(() => {
        return organisation?.googleDriveQuoteStatusTrigger
            ? organisation.googleDriveQuoteStatusTrigger
                  .map((status) => ({
                      value: status,
                      label: QUOTE_STATUS_MAP[status]?.label,
                      index: QUOTE_STATUS_MAP[status]?.index,
                  }))
                  .sort((a, b) => a.index - b.index)
            : []
    }, [organisation?.googleDriveQuoteStatusTrigger])

    // Local state
    const [isConnected, setIsConnected] = useState(googleDriveEnabled)
    const [folderName, setFolderName] = useState(googleDriveSubFolder)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)
    const [accessToken, setAccessToken] = useState(null)
    const [googleClient, setGoogleClient] = useState(null)
    const [openEditStatusesDialog, setOpenEditStatusesDialog] = useState(false)

    // API hooks
    const [updateOrganisation, { isLoading: isUpdating }] = useUpdateOrganisationMutation()
    const [connectGoogleDrive] = useLazyConnectGoogleDriveQuery()
    const [disconnectGoogleDrive] = useDisconnectGoogleDriveMutation()
    const [getGoogleDriveToken] = useLazyGetGoogleDriveTokenQuery()
    // Custom hook for Google API loading
    const { apiLoaded, loadingError } = useGoogleApi()

    // Update the organization Google Drive connection details
    const updateGoogleDriveConnection = useCallback(
        async ({ selectedFolderId, selectedFolderName }) => {
            if (!organisation || !organisationId) return

            try {
                await updateOrganisation({
                    organisationDto: {
                        organisationId,
                        googleDriveSubFolder: selectedFolderName,
                        googleDriveSubFolderId: selectedFolderId,
                    },
                }).unwrap()
            } catch (error) {
                console.error('Failed to update organisation:', error)
                enqueueSnackbar(t('Failed to update settings'), { variant: 'error' })
            }
        },
        [organisation, organisationId, dispatch, updateOrganisation, enqueueSnackbar, t]
    )

    // Callback for handling folder selection from Google Drive Picker
    const pickerCallback = useCallback(
        async (data) => {
            if (!data || !data.action) return

            if (data.action === window.google.picker.Action.PICKED) {
                const folder = data.docs[0]

                // Input validation
                if (!folder || !folder.id) {
                    setError(t('Invalid folder selection'))
                    return
                }

                // Ensure it's a folder
                if (folder.mimeType && folder.mimeType !== 'application/vnd.google-apps.folder') {
                    setError(t('Please select a folder, not a file'))
                    return
                }

                // Update state and persist changes
                setFolderName(folder.name)
                await updateGoogleDriveConnection({
                    selectedFolderName: folder.name,
                    selectedFolderId: folder.id,
                })

                enqueueSnackbar(t('Folder {{folderName}} selected successfully', { folderName: folder.name }), {
                    variant: 'success',
                })
            }
        },
        [updateGoogleDriveConnection, t, enqueueSnackbar]
    )

    // Initialize Google OAuth client
    const initGoogleClient = useCallback(() => {
        if (!window.google || !window.google.accounts?.oauth2) {
            setError(t('Google Identity Services not available'))
            return null
        }

        try {
            const client = window.google.accounts.oauth2.initCodeClient({
                client_id: CLIENT_ID,
                scope: SCOPES,
                ux_mode: 'popup',
                include_granted_scopes: true,
                enable_granular_consent: true,
                callback: async (response) => {
                    if (response.error) {
                        console.error('Auth code error:', response.error)
                        setError(t('Authentication failed: {{error}}', { error: response.error }))
                        setIsLoading(false)
                        return
                    }

                    if (response.code) {
                        try {
                            // Exchange the auth code for tokens via backend
                            const tokenResponse = await connectGoogleDrive({
                                organisationId,
                                code: response.code,
                            }).unwrap()

                            if (!tokenResponse || tokenResponse.error) {
                                setError(
                                    t('Token exchange failed: {{error}}', {
                                        error: tokenResponse?.error || 'Unknown error',
                                    })
                                )
                                setIsLoading(false)
                                return
                            }

                            // Set the token for GAPI client to use
                            if (tokenResponse) {
                                if (window.gapi?.client) {
                                    window.gapi.client.setToken({
                                        access_token: tokenResponse,
                                    })
                                }

                                setAccessToken(tokenResponse)
                                setIsConnected(true)

                                enqueueSnackbar(t('Connected to Google Drive successfully'), { variant: 'success' })
                            }
                        } catch (error) {
                            console.error('Token exchange error:', error)
                            setError(t('Failed to exchange authorization code for token'))
                        } finally {
                            setIsLoading(false)
                        }
                    }
                },
                error_callback: (error) => {
                    console.error('Error during Google OAuth:', error?.message)
                    setError(t('Google OAuth error: {{message}}', { message: error?.message }))
                    setIsLoading(false)
                },
            })

            setGoogleClient(client)
            return client
        } catch (err) {
            console.error('Error initializing code client:', err)
            setError(t('Failed to initialize authentication: {{message}}', { message: err.message }))
            setIsLoading(false)
            return null
        }
    }, [connectGoogleDrive, organisationId, folderName, updateGoogleDriveConnection, enqueueSnackbar, t])

    // Handle Google Drive connection
    const handleConnect = useCallback(() => {
        setIsLoading(true)
        setError(null)

        try {
            // Initialize client if needed
            let client = googleClient
            if (!client) {
                client = initGoogleClient()
                if (!client) {
                    throw new Error(t('Failed to initialize Google client'))
                }
            }

            // Request authorization code
            client.requestCode()
        } catch (err) {
            console.error('Google auth error:', err)
            setError(t('Failed to connect to Google Drive: {{message}}', { message: err.message || '' }))
            setIsLoading(false)
        }
    }, [googleClient, initGoogleClient, t])

    // Show Google Drive folder picker
    const showDrivePicker = useCallback(
        (accessToken) => {
            if (!window.google || !window.google.picker) {
                setError(t('Google Picker API not loaded'))
                return
            }

            if (!accessToken) {
                setError(t('Not authenticated with Google'))
                handleConnect()
                return
            }

            try {
                // Create folder view - make sure we can select folders
                const folderView = new window.google.picker.DocsView(window.google.picker.ViewId.FOLDERS)
                    .setParent('root')
                    .setIncludeFolders(true)
                    .setSelectFolderEnabled(true)
                    .setMode(window.google.picker.DocsViewMode.LIST)

                // Create and render the picker with multiple views
                new window.google.picker.PickerBuilder()
                    .addView(folderView)
                    .setOAuthToken(accessToken)
                    .setDeveloperKey(API_KEY)
                    .setAppId(APP_ID)
                    .setCallback(pickerCallback)
                    .setTitle(t('Select a folder'))
                    .setSelectableMimeTypes('application/vnd.google-apps.folder')
                    .setOrigin(window.location.origin)
                    .build()
                    .setVisible(true)
            } catch (err) {
                console.error('Picker error:', err)
                setError(t('Failed to open folder picker: {message}', { message: err.message }))
            }
        },
        [pickerCallback, t, handleConnect]
    )

    // Handle disconnecting from Google Drive
    const handleDisconnect = useCallback(async () => {
        setIsLoading(true)

        try {
            // Clear the token from gapi client
            if (window.gapi && window.gapi.client) {
                window.gapi.client.setToken(null)
            }

            // Update the backend
            await disconnectGoogleDrive({ organisationId }).unwrap()

            // Reset component state
            setIsConnected(false)
            setFolderName(null)
            setAccessToken(null)
            setError(null)

            enqueueSnackbar(t('Disconnected from Google Drive'), { variant: 'info' })
        } catch (err) {
            setError(t('Error disconnecting from Google Drive'))
            console.error('Disconnect error:', err)
        } finally {
            setIsLoading(false)
        }
    }, [disconnectGoogleDrive, organisationId, enqueueSnackbar, t, updateGoogleDriveConnection])

    // Handle opening folder picker
    const handleOpenFolderPicker = useCallback(async () => {
        if (accessToken) {
            return showDrivePicker(accessToken)
        }

        try {
            const tokenResponse = await getGoogleDriveToken({ organisationId }).unwrap()

            if (window.gapi?.client) {
                window.gapi.client.setToken({
                    access_token: tokenResponse.accessToken,
                })
            }

            setAccessToken(tokenResponse.accessToken)
            setIsConnected(true)
            showDrivePicker(tokenResponse.accessToken)
        } catch (error) {
            console.error('Error fetching token:', error)
            setError(t('Failed to fetch Google Drive token'))
        }
    }, [showDrivePicker, handleConnect, accessToken])

    // Handle opening status edit dialog
    const handleOpenEditStatusesDialog = () => {
        setOpenEditStatusesDialog(true)
    }

    // Handle saving selected statuses
    const handleSaveStatuses = async (enabledStatuses) => {
        setOpenEditStatusesDialog(false)

        const selectedStatuses = enabledStatuses.map((status) => status.value)

        try {
            await updateOrganisation({
                organisationDto: {
                    organisationId,
                    googleDriveQuoteStatusTrigger: selectedStatuses,
                },
            }).unwrap()
        } catch (error) {
            console.error('Failed to update organisation:', error)
            enqueueSnackbar(t('Failed to update settings'), { variant: 'error' })
        }
    }

    // Set error from API loading if applicable
    useEffect(() => {
        if (loadingError) {
            setError(loadingError)
        }
    }, [loadingError])

    // Update component state when organisation data changes
    useEffect(() => {
        setIsConnected(googleDriveEnabled)
        setFolderName(googleDriveSubFolder)
    }, [googleDriveEnabled, googleDriveSubFolder])

    // Initialize Google client when API is loaded
    useEffect(() => {
        if (apiLoaded && !googleClient) {
            const client = initGoogleClient()
            if (client) {
                setGoogleClient(client)
            }
        }
    }, [apiLoaded, googleClient, initGoogleClient])

    return (
        <Card
            elevation={0}
            sx={styles.card}
            variant="outlined"
        >
            <CardContent sx={styles.content}>
                <Box sx={styles.header}>
                    <img
                        alt="Google Drive Icon"
                        height={32}
                        src="google-drive.svg"
                        style={{ filter: isConnected ? 'none' : 'grayscale(100%)' }}
                    />
                    <Typography variant="h6">{t('Google Drive')}</Typography>
                </Box>

                {isConnected ? (
                    <Typography variant="body2">
                        <Typography variant="body2">
                            {t(
                                "When a quote's status changes to one of the selected statuses, a zip file containing the quote's CSV and drawing files will be saved to your selected Google Drive folder."
                            )}
                        </Typography>
                    </Typography>
                ) : (
                    <Typography variant="body2">
                        {t('Connect to Google Drive to automatically upload your quote files.')}
                    </Typography>
                )}

                {error ? (
                    <Alert
                        severity="error"
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                ) : null}

                {isConnected ? (
                    <>
                        <Box>
                            <Box
                                alignItems="center"
                                display={'flex'}
                                justifyContent="space-between"
                            >
                                <Typography
                                    mb={1}
                                    variant="strong2"
                                >
                                    {t('Selected quote status(es):')}
                                </Typography>
                                <IconButton
                                    aria-label="Edit statuses"
                                    color="secondary"
                                    disabled={isUpdating}
                                    title="Edit statuses"
                                    onClick={handleOpenEditStatusesDialog}
                                >
                                    {isUpdating ? <CircularProgress size={20} /> : <Edit fontSize="small" />}
                                </IconButton>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {enabledStatuses.map((status) => (
                                    <QuoteStatusChip
                                        key={status.value}
                                        quoteStatus={status.value}
                                    />
                                ))}
                            </Box>
                        </Box>

                        <Box>
                            <Typography
                                mb={1}
                                variant="strong2"
                            >
                                {t('Selected folder:')}
                            </Typography>
                            <Box sx={styles.folderDisplay}>
                                <Folder02Icon variant="bulk" />
                                <Typography>{folderName || t('No folder selected')}</Typography>
                            </Box>

                            <Box sx={styles.actions}>
                                <Button
                                    disabled={!apiLoaded || isLoading}
                                    startIcon={<FolderSyncIcon variant="bulk" />}
                                    title={t('Select a folder')}
                                    variant="outlined"
                                    fullWidth
                                    onClick={handleOpenFolderPicker}
                                >
                                    {folderName ? t('Change Folder') : t('Select Folder')}
                                </Button>
                                <Button
                                    color="error"
                                    disabled={isLoading}
                                    startIcon={
                                        isLoading ? <CircularProgress size={20} /> : <Unlink04Icon variant="bulk" />
                                    }
                                    title={t('Disconnect from Google Drive')}
                                    variant="outlined"
                                    fullWidth
                                    onClick={handleDisconnect}
                                >
                                    {t('Disconnect')}
                                </Button>
                            </Box>
                        </Box>
                    </>
                ) : (
                    <Button
                        color="secondary"
                        disabled={disabled || isLoading || !apiLoaded}
                        startIcon={
                            <Link03Icon
                                size={18}
                                variant="bulk"
                            />
                        }
                        variant="contained"
                        onClick={handleConnect}
                    >
                        {t('Connect to Google Drive')}
                    </Button>
                )}
            </CardContent>

            {openEditStatusesDialog ? (
                <EditGoogleDriveStatusesDialog
                    open={openEditStatusesDialog}
                    options={googleDriveStatusesOptions}
                    statuses={enabledStatuses}
                    onClose={() => setOpenEditStatusesDialog(false)}
                    onSave={handleSaveStatuses}
                />
            ) : null}
        </Card>
    )
}

GoogleDriveIntegration.propTypes = {
    disabled: PropTypes.bool,
}

export default GoogleDriveIntegration
